import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useTranslation } from "react-i18next";

import type { EmployeeGroup } from "~/providers/employee-group-provider";
import { useEmployeeGroupManager } from "~/managers/use-employee-group-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useEmployeeGroupStore } from "~/stores/use-employee-group-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import EmployeeGroupForm from "./employee-group-form";

export default function EmployeeGroupDialog() {
  const { t } = useTranslation("common");

  const {
    employeeGroupDialogOpen,
    setSelectedEmployeeGroup,
    setEmployeeGroupDialogOpen,
    selectedEmployeeGroup,
  } = useEmployeeGroupStore();

  const { checkUnsavedChanges } = useUnsavedChanges();

  const { deleteEmployeeGroup } = useEmployeeGroupManager();

  const handleDialog = (open: boolean, force?: boolean) => {
    if (open === false) {
      if (force !== true) {
        const result = checkUnsavedChanges();
        if (result === true) {
          setEmployeeGroupDialogOpen(true);
          return;
        }
      }
      setSelectedEmployeeGroup(undefined);
    }
    setEmployeeGroupDialogOpen(open);
  };

  const handleDelete = async (employeeGroup: EmployeeGroup) => {
    const result = await deleteEmployeeGroup(employeeGroup);

    if (result === true) {
      handleDialog(false, true);
    }
  };

  return (
    <Dialog open={employeeGroupDialogOpen} onOpenChange={handleDialog}>
      <DialogContent className="max-w-xl w-full">
        <DialogHeader>
          <DialogTitle>{t("EmployeeGroup.employee_group")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
          <EmployeeGroupForm
            onClose={(force) => {
              handleDialog(false, force);
            }}
            employeeGroup={selectedEmployeeGroup}
          />
        </div>
        <DialogFooter>
          {selectedEmployeeGroup && !selectedEmployeeGroup.isDefault && (
            <Button
              variant="destructive"
              onClick={() => void handleDelete(selectedEmployeeGroup)}
            >
              {t("delete")}
            </Button>
          )}
          <Button form="employee-group-form" type="submit" variant="success">
            {selectedEmployeeGroup ? t("update") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
